import { Bot, ArrowDown, ArrowUpRight } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import type { PublicProjectSummary } from "@/lib/public-project";
import { initials } from "@/lib/account";
import { featuredDisplayNames, featuredProjectNames, industryProjects, projectDisplayName } from "@/lib/marketing-projects";
import { BrandMark } from "../brand-mark";

export function AgentIcon({
  agent,
}: {
  agent: "openai" | "claude" | "custom";
}) {
  return agent === "custom" ? (
    <Bot size={20} strokeWidth={1.5} aria-hidden="true" />
  ) : (
    <span
      className={`mk-agent-icon mk-agent-icon-${agent}`}
      aria-hidden="true"
    />
  );
}

export function HomeAction({
  href,
  children,
  kind = "primary",
}: {
  href: string;
  children: ReactNode;
  kind?: "primary" | "secondary" | "tertiary";
}) {
  return (
    <a className={`mk-home-button mk-home-button-${kind}`} href={href}>
      {children}
    </a>
  );
}

const fallbackArtwork: Record<string, string> = {
  "Northstar Metrics": "northstar",
  "Atelier Sola": "atelier",
  Fragments: "fragments",
  "Morrow Goods": "morrow",
};

function recentUpdate(value?: string) {
  if (!value) return "recently";
  const minutes = Math.max(0, Math.round((Date.now() - new Date(value).getTime()) / 60_000));
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  if (days === 1) return "yesterday";
  return days < 7 ? `${days} days ago` : new Date(value).toLocaleDateString();
}

function usePublicProjects(endpoint: "/api/public/featured-projects" | "/api/public/projects") {
  const [projects, setProjects] = useState<PublicProjectSummary[] | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    void fetch(endpoint, { signal: controller.signal })
      .then(response => response.ok ? response.json() as Promise<PublicProjectSummary[]> : Promise.reject())
      .then(setProjects)
      .catch(() => undefined);
    return () => controller.abort();
  }, [endpoint]);
  return projects;
}

function orderedProjects(projects: PublicProjectSummary[], names: readonly string[]) {
  const byName = new Map(projects.map(project => [project.name, project]));
  return names.flatMap(name => byName.get(name) ? [byName.get(name)!] : []);
}

function ProjectCard({ project, fallbackName, community = false }: {
  project?: PublicProjectSummary;
  fallbackName?: string;
  community?: boolean;
}) {
  const name = projectDisplayName(project?.name || fallbackName || "Project");
  const href = project ? `/p/${encodeURIComponent(project.slug)}` : undefined;
  const artwork = fallbackArtwork[name];
  const preview = project ? <img
    className="mk-project-preview"
    src={`/api/public/projects/${encodeURIComponent(project.slug)}/thumbnail`}
    width="400"
    height="300"
    alt={project.description || `${name} project preview`}
  /> : artwork ? <img
    className="mk-project-preview"
    src={`/marketing/${artwork}.svg`}
    width="400"
    height="300"
    alt={`${name} project preview`}
  /> : <span className={`mk-project-preview mk-project-placeholder is-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} aria-hidden="true"><b>{name}</b></span>;
  const content = <>
    {preview}
    <span className="mk-project-card-body">
      <strong>{name}</strong>
      {community && project?.description && <span>{project.description}</span>}
      <small>Updated {recentUpdate(project?.updatedAt)}</small>
    </span>
    <span className="mk-project-author" title={project?.author?.name || "Proxima Forma community"}>
      {project?.author?.avatar ? <img src={project.author.avatar} alt="" /> : initials(project?.author?.name || "Proxima Forma")}
    </span>
  </>;
  return <article>{href ? <a href={href} aria-label={`Explore ${name}`}>{content}</a> : <div>{content}</div>}</article>;
}

export function FeaturedProjectGrid({
  community = false,
  publishedOnly = false,
}: {
  community?: boolean;
  publishedOnly?: boolean;
}) {
  const liveProjects = usePublicProjects("/api/public/featured-projects");
  const selected = liveProjects ? orderedProjects(liveProjects, featuredProjectNames) : [];
  if (publishedOnly && !selected.length) return null;
  return <div className="mk-home-projects">
    {selected.length
      ? selected.map(project => <ProjectCard key={project.slug} project={project} community={community} />)
      : featuredDisplayNames.map(name => <ProjectCard key={name} fallbackName={name} community={community} />)}
  </div>;
}

export function IndustryProjectGrid({ projectNames }: { projectNames: readonly string[] }) {
  const liveProjects = usePublicProjects("/api/public/projects");
  const selected = liveProjects ? orderedProjects(liveProjects, projectNames) : [];
  return <div className="mk-home-projects mk-industry-projects">
    {selected.length
      ? selected.map(project => <ProjectCard key={project.slug} project={project} />)
      : projectNames.map(name => <ProjectCard key={name} fallbackName={name} />)}
  </div>;
}

export function CommunityFeaturedProjects() {
  const liveProjects = usePublicProjects("/api/public/featured-projects");
  const selected = liveProjects ? orderedProjects(liveProjects, featuredProjectNames) : [];
  if (!selected.length) return null;
  return <section className="mk-community-featured" aria-labelledby="community-featured-title">
    <h2 id="community-featured-title">Featured community content</h2>
    <div className="mk-home-projects">{selected.map(project => <ProjectCard key={project.slug} project={project} community />)}</div>
  </section>;
}

export const announcements = [
  {
    slug: "shared-design-canvas",
    image: "shared-canvas",
    title: "Meet your shared design canvas",
    description:
      "Shape ideas together with your agents. Refine every layer by hand.",
    alt: "A shared design canvas on a laptop beside a notebook of interface sketches.",
    category: "Product",
  },
  {
    slug: "bring-your-agent",
    image: "connected-agents",
    title: "Bring the agent you already use",
    description:
      "Connect ChatGPT, Claude, or your custom agent. Keep working with the tools you already know.",
    alt: "Two softly sculpted forms connected by a dark link.",
    category: "Guides",
  },
] as const;

export function AnnouncementCards({ filter = "All" }: { filter?: string }) {
  return (
    <div className="mk-home-stories">
      {announcements
        .filter((story) => filter === "All" || story.category === filter)
        .map((story) => (
          <a
            href={`/blog/${story.slug}`}
            className="mk-home-story"
            key={story.slug}
          >
            <img
              src={`/marketing/${story.image}.jpg`}
              width="1312"
              height="656"
              alt={story.alt}
            />
            <h3>
              {story.title} <ArrowUpRight size={18} aria-hidden="true" />
            </h3>
            <p>{story.description}</p>
          </a>
        ))}
    </div>
  );
}

const features = [
  [
    "One shared canvas",
    "You and your agents work in the same visual space, with the same context.",
  ],
  [
    "Always editable",
    "Agent output stays as real layers and components—not flattened screenshots.",
  ],
  [
    "Bring references",
    "Give your agents visual references so they can follow the direction instead of guessing.",
  ],
  [
    "Works with your favorite agent",
    "Keep using ChatGPT, Claude, or your custom agent. Don’t reinvent the wheel—bring the tooling you already use.",
  ],
  [
    "Fits your current workflow",
    "Add Proxima Forma to the way you already work instead of rebuilding your process around another tool.",
  ],
  [
    "Share and collaborate",
    "Bring people and agents into the same design space, share progress, and keep everyone working from the same source.",
  ],
  [
    "Pay as you go",
    "Pay for what you consume as you work. Start small, scale naturally, and avoid paying for unused capacity.",
  ],
  [
    "Keep your agent tooling",
    "Use the prompts, models, integrations, and agent stack you already trust—just give them a real design canvas.",
  ],
] as const;

const prompts = [
  {
    agent: "openai",
    text: "Design a calm landing page for a ceramics studio.",
  },
  {
    agent: "claude",
    text: "Turn these references into a clear onboarding flow.",
  },
  {
    agent: "custom",
    text: "Document the components and their interaction states.",
  },
] as const;
const outputs = [
  "Screen layouts",
  "Component states",
  "Colors & typography",
  "User journeys",
  "Design history",
  "Design decisions",
];
const consumers = [
  "Coding agent",
  "Marketing agent",
  "Producer agent",
  "Product agent",
];

// Coordinates follow the editable “How it works” diagram in the design project.
const connections = [
  [336, 190, 440, 268],
  [336, 302, 440, 302],
  [336, 414, 440, 334],
  [568, 260, 672, 152],
  [568, 276, 672, 212],
  [568, 292, 672, 272],
  [568, 308, 672, 332],
  [568, 324, 672, 392],
  [568, 340, 672, 452],
  [944, 152, 1016, 146],
  [944, 212, 1016, 158],
  [944, 272, 1016, 170],
  [944, 280, 1016, 254],
  [944, 332, 1016, 350],
  [944, 392, 1016, 442],
  [944, 452, 1016, 454],
];

function FlowDiagram() {
  return (
    <div
      className="mk-home-flow"
      aria-label="Your prompts become editable designs in Proxima Forma, ready for your agents to use"
    >
      <svg
        className="mk-flow-lines"
        viewBox="0 80 1328 444"
        fill="none"
        aria-hidden="true"
      >
        {[222, 176, 130, 84].map((r, i) => (
          <circle
            key={r}
            cx="504"
            cy="302"
            r={r}
            stroke="currentColor"
            opacity={[0.035, 0.07, 0.12, 0.2][i]}
          />
        ))}
        <g opacity="0.4" stroke="currentColor" strokeWidth="1.5">
          {connections.map(([x1, y1, x2, y2], i) => {
            const bend = x1 + (x2 - x1) * 0.48;
            return (
              <g key={i}>
                <path
                  d={`M ${x1} ${y1} C ${bend} ${y1} ${bend} ${y2} ${x2 - 18} ${y2} H ${x2 - 8}`}
                />
                <path
                  d={`M ${x2 - 8} ${y2 - 4} L ${x2} ${y2} L ${x2 - 8} ${y2 + 4} Z`}
                  fill="currentColor"
                  stroke="none"
                />
              </g>
            );
          })}
        </g>
      </svg>
      <div className="mk-flow-prompts">
        {prompts.map(({ agent, text }, i) => (
          <div className="mk-flow-prompt" key={agent}>
            <AgentIcon agent={agent} />
            <p>{text}</p>
            <img
              src={`/marketing/avatar-${i + 1}.jpg`}
              width="32"
              height="32"
              alt=""
              loading="lazy"
            />
          </div>
        ))}
      </div>
      <ArrowDown className="mk-flow-mobile-arrow" aria-hidden="true" />
      <div className="mk-flow-brand" role="img" aria-label="Proxima Forma">
        <BrandMark />
      </div>
      <ArrowDown className="mk-flow-mobile-arrow" aria-hidden="true" />
      <ul className="mk-flow-outputs" aria-label="Editable design context">
        {outputs.map((text) => (
          <li key={text}>{text}</li>
        ))}
      </ul>
      <ArrowDown className="mk-flow-mobile-arrow" aria-hidden="true" />
      <ul
        className="mk-flow-consumers"
        aria-label="Agents that use the design context"
      >
        {consumers.map((text) => (
          <li key={text}>
            <Bot size={24} strokeWidth={1.5} aria-hidden="true" />
            {text}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function HomePage() {
  return (
    <div className="mk-home mk-home-container">
      <section className="mk-home-hero" aria-labelledby="home-title">
        <h1 id="home-title">Design at the speed of AI</h1>
        <p>Your creative direction, agents do the work.</p>
        <div className="mk-home-actions">
          <HomeAction href="/connect/chatgpt">
            Use with ChatGPT <AgentIcon agent="openai" />
          </HomeAction>
          <HomeAction href="/connect/claude" kind="secondary">
            Use with Claude <AgentIcon agent="claude" />
          </HomeAction>
          <HomeAction href="/connect/agent" kind="tertiary">
            Custom agent <AgentIcon agent="custom" />
          </HomeAction>
        </div>
      </section>

      <section
        className="mk-home-section"
        id="featured-projects"
        aria-labelledby="featured-title"
      >
        <h2 id="featured-title">Featured projects</h2>
        <FeaturedProjectGrid />
        <p className="mk-home-submit">
          <a href="/community#submit-project">Submit your projects</a> to be
          featured.
        </p>
      </section>

      <section className="mk-home-section" aria-labelledby="features-title">
        <h2 id="features-title">Built for the way you create</h2>
        <div className="mk-home-features">
          {features.map(([title, text]) => (
            <article key={title}>
              <h3>{title}</h3>
              <p>{text}</p>
            </article>
          ))}
        </div>
      </section>

      <section
        className="mk-home-section"
        id="how-it-works"
        aria-labelledby="how-title"
      >
        <h2 id="how-title">How it works</h2>
        <FlowDiagram />
      </section>

      <section className="mk-home-section mk-home-industries" aria-labelledby="home-industries-title">
        <h2 id="home-industries-title">Use cases by industry</h2>
        <div className="mk-industry-filters">
          {industryProjects.map(industry => <a key={industry.slug} href={`/use-cases/${industry.slug}`}>{industry.label}</a>)}
        </div>
      </section>
    </div>
  );
}
